import { Avatar, Box, Button, Card, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { ButtonStyle, CardImg } from './style'
import { formatNumber } from '../../../../common/FormatNumber'
import { IconComments } from '../../../../icons/IconComments'
import { IconParticipationProjects } from '../../../../icons/IconParticipationProjects'
import { StyledBadge } from '../../../ui-kit'
import { SocialOrderInfoProps } from '../../../../types/entitys/socialOrders'
import { routesProfile } from '../../../../consts'
import { NavLink, useNavigate } from 'react-router-dom'
import { MouseEvent } from 'react'
import { formatApiDateToDdMmYyyy } from '../../../../utils/common'
import { resolveSocialOrderCommentsTotal } from '../../../../api/types/socialOrders'
import { SocialOrderLikeControl } from '../../SocialOrderLikeControl'
import { useSocialOrderEngagement } from '../../../../hooks/useSocialOrderEngagement'
import {
  getUserInitialsFromDisplayName,
  resolveUserAvatarUrl,
  resolveUserDisplayName,
} from '../../../../utils/deletedUserDisplay'

export const SocialOrderInfoMobile = ({ socialOrder }: SocialOrderInfoProps) => {
  const navigate = useNavigate()
  const cacheKey = socialOrder.slug || socialOrder.id
  const {
    isAuthor,
    isSubscribed,
    isLiked,
    likesCount,
    toggleSubscribe,
    toggleLike,
    isSubscribeLoading,
    isLikeLoading,
  } = useSocialOrderEngagement({ socialOrder, cacheKey })
  const authorName = resolveUserDisplayName(socialOrder.author)
  const viewsCount = socialOrder.counters.views
  const commentsCount = resolveSocialOrderCommentsTotal(socialOrder.counters.comments)
  const focusLabel = Array.isArray(socialOrder.focus) ? socialOrder.focus.join(', ') : ''

  const handleToggleSubscribe = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    void toggleSubscribe()
  }

  const handleCreateProjectFromSocialOrder = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    navigate(`/${routesProfile.PROJECTS.path}/create?socialOrderId=${socialOrder.id}`)
  }

  return (
    <Card variant="primaryAdaptiveCard">
      <Typography variant="black-16-semiBold" component="div" mb={1}>
        Автор
      </Typography>
      <Stack direction="row" mb={1.5} alignItems="center">
        <StyledBadge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="dot"
          size={12}
          invisible={socialOrder.author?.is_online !== true}
        >
          <Avatar src={resolveUserAvatarUrl(socialOrder.author)} sx={CardImg}>
            {getUserInitialsFromDisplayName(authorName)}
          </Avatar>
        </StyledBadge>
        <Typography variant="black-14-medium" ml={2} noWrap>{authorName}</Typography>
      </Stack>

      <Stack direction="column">
        <Typography variant="black-16-semiBold" mb={1}>
          Соцзаказ
        </Typography>
        {isAuthor ? (
          <Button
            variant="follow"
            component={NavLink}
            to={`/${routesProfile.SOCIALORDERS.path}/${socialOrder.id}/edit`}
            sx={{ ...ButtonStyle, mb: 1 }}
          >
            Редактировать
          </Button>
        ) : (
          <Button
            variant="follow"
            sx={{ ...ButtonStyle, mb: 1 }}
            disabled={isSubscribeLoading}
            onClick={handleToggleSubscribe}
          >
            {isSubscribed ? 'Подписан' : 'Подписаться'}
          </Button>
        )}
        {focusLabel ? (
          <Typography variant="black-14-light" sx={{ lineHeight: '18px' }}>
            Направленность: {focusLabel}
          </Typography>
        ) : null}
        <Grid container mb={1}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="black-14-light" sx={{ lineHeight: '18px' }}>
              Создан: {formatApiDateToDdMmYyyy(socialOrder.created_at)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="black-14-light" sx={{ lineHeight: '18px' }}>
              Отредактирован: {formatApiDateToDdMmYyyy(socialOrder.updated_at)}
            </Typography>
          </Grid>
        </Grid>

        <Typography variant="black-14-medium" mb={1}>Просмотров: {formatNumber(viewsCount)}</Typography>

        <Button
          variant="contained"
          color="secondary"
          sx={{ ...ButtonStyle, mb: 0.5 }}
          startIcon={<IconParticipationProjects sx={{ width: '24px', height: '24px' }} />}
          onClick={handleCreateProjectFromSocialOrder}
        >
          Создать проект из соцзаказа
        </Button>
        <Stack direction="row" gap={{ xs: 2, sm: 1 }} alignItems="center">
          <Box onClick={(e) => e.stopPropagation()}>
            <SocialOrderLikeControl
              socialOrderId={socialOrder.id}
              isLiked={isLiked}
              likesCount={likesCount}
              likesPreview={socialOrder.likes_preview}
              onToggleLike={toggleLike}
              loading={isLikeLoading}
              compact
            />
          </Box>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <IconComments fontSize="small" />
            <Typography variant="black-14-medium">{formatNumber(commentsCount)}</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  )
}
